import { createId } from "@paralleldrive/cuid2";
import {
  basicsSchema,
  defaultBasics,
  defaultMetadata,
  defaultResumeData,
  defaultSections,
  type ResumeData,
  resumeDataSchema,
} from "@reactive-resume/schema";
import type { Json } from "@reactive-resume/utils";
import type { Schema } from "zod";

import type { Parser } from "../interfaces/parser";

// Typed views over raw JSON so named-property access satisfies noPropertyAccessFromIndexSignature
type RawResume = { basics?: unknown; sections?: unknown; metadata?: unknown };
type RawSection = { id?: unknown; items?: unknown } & Record<string, unknown>;
type RawSections = { custom?: unknown } & Record<string, unknown>;
type RawItem = { id?: unknown } & Record<string, unknown>;

export class ReactiveResumeParser implements Parser<Json, ResumeData> {
  schema: Schema;

  constructor() {
    this.schema = resumeDataSchema;
  }

  readFile(file: File): Promise<Json> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      // eslint-disable-next-line unicorn/prefer-add-event-listener
      reader.onload = () => {
        try {
          const result = JSON.parse(reader.result as string) as Json;
          resolve(result);
        } catch {
          reject(new Error("Failed to parse JSON"));
        }
      };

      // eslint-disable-next-line unicorn/prefer-add-event-listener
      reader.onerror = () => {
        reject(new Error("Failed to read the file"));
      };

      // eslint-disable-next-line unicorn/prefer-blob-reading-methods
      reader.readAsText(file);
    });
  }

  validate(data: Json) {
    // Attempt 1: strict validation
    const strict = resumeDataSchema.safeParse(data);
    if (strict.success) return strict.data;

    // Attempt 2: normalize then retry (handles old UUID IDs, missing sections, etc.)
    const normalized = normalizeResumeJson(data);
    const retry = resumeDataSchema.safeParse(normalized);
    if (retry.success) return retry.data;

    // Attempt 3: return defaults + whatever basics survive schema validation
    const basicsParsed = basicsSchema.safeParse((data as RawResume).basics);
    return {
      ...defaultResumeData,
      basics: basicsParsed.success ? basicsParsed.data : defaultBasics,
    } as ResumeData;
  }

  convert(data: ResumeData) {
    return data;
  }
}

function fixItems(items: unknown): unknown[] {
  if (!Array.isArray(items)) return [];
  return items.map((item) => {
    if (typeof item !== "object" || item === null) return item;
    const obj = item as RawItem;
    const id = typeof obj.id === "string" && obj.id.length > 0 ? obj.id : "";
    // Replace if not a valid cuid2-length string (cuid2 is 24 chars, starts with letter)
    const needsNewId = !/^[a-z][\da-z]{23}$/.test(id);
    return needsNewId ? { ...obj, id: createId() } : obj;
  });
}

function normalizeResumeJson(data: Json): Record<string, unknown> {
  const obj = data as RawResume;

  // Merge sections with defaults to fill any missing required keys
  const rawSections =
    typeof obj.sections === "object" && obj.sections !== null
      ? (obj.sections as RawSections)
      : ({} as RawSections);

  const sections: Record<string, unknown> = { ...defaultSections };
  for (const key of Object.keys(defaultSections) as (keyof typeof defaultSections)[]) {
    const existing = rawSections[key];
    if (typeof existing === "object" && existing !== null) {
      const s = existing as RawSection;
      const def = defaultSections[key] as RawSection;
      const merged: RawSection = {
        ...def,
        ...s,
        id: def.id,
      };
      if ("items" in merged) merged.items = fixItems(s.items);
      sections[key] = merged;
    }
  }
  if (typeof rawSections.custom === "object" && rawSections.custom !== null) {
    Object.assign(sections, { custom: rawSections.custom });
  }

  const rawMetadata =
    typeof obj.metadata === "object" && obj.metadata !== null
      ? (obj.metadata as Record<string, unknown>)
      : {};

  return {
    ...(data as Record<string, unknown>),
    sections,
    metadata: { ...defaultMetadata, ...rawMetadata },
  };
}
